import { hasMinimumEssence, resolveEssenceShortfall, type EssencePool } from '@sorrem/shared';
import type { UIInventoryItem } from '@sorrem-client/types';
import { Button } from '@sorrem-client/ui/components';
import { describeShortfall, findUpgradeTarget, resolveUpgradePath } from '../../crafting-preview';
import styles from './WorkbenchUpgrade.module.scss';

interface WorkbenchUpgradeProps {
    readonly item: UIInventoryItem;
    readonly currentLevel: number;
    /** The level chosen beside the item; its own level is the Current card. */
    readonly targetLevel: number;
    readonly banked: Partial<EssencePool>;
    /** The tab is busy upgrading or rerolling. */
    readonly isWorking: boolean;
    /** Why this item's last upgrade failed, or null. */
    readonly error: string | null;
    /** Upgrades the item this many levels. */
    readonly onUpgrade: (levels: number) => void;
}

/**
 * The upgrade to the chosen level (#287), under the item's card: what it
 * costs from now, paying every level on the way, and doing it. While the
 * Current card is chosen it asks for a level instead.
 */
export function WorkbenchUpgrade({ item, currentLevel, targetLevel, banked, isWorking, error, onUpgrade }: WorkbenchUpgradeProps): JSX.Element | null {
    const path = resolveUpgradePath(item);
    if (path.every((row) => row.isReached)) {
        return null;
    }
    const total = findUpgradeTarget(path, targetLevel)?.total ?? null;
    if (total === null) {
        return <p className={styles.note}>Pick an upgrade level to see what it costs.</p>;
    }
    const shortfall = describeShortfall(resolveEssenceShortfall(banked, total));

    return (
        <section className={styles.workbenchUpgrade} aria-label='Upgrade'>
            <span className={styles.target}>{`${item.name} +${currentLevel} to +${targetLevel}`}</span>
            <Button disabled={isWorking || !hasMinimumEssence(banked, total)} onClick={() => onUpgrade(targetLevel - currentLevel)}>
                {`Upgrade to +${targetLevel}`}
            </Button>
            {shortfall !== null && <p className={styles.problem}>{shortfall}</p>}
            {error !== null && <p className={styles.problem} role='alert'>{error}</p>}
        </section>
    );
}
